/**
 * assets/events-rsvp.js
 * Handles RSVP modal open/close and form submission for the Events section.
 *
 * Submission strategy:
 * Posts to a Google Apps Script Web App (see /apps-script/Code.gs) bound to a
 * Google Sheet. Each RSVP is appended as a new row, and a notification email
 * is sent (per-event, using that block's notification_email if set).
 *
 * Two Apps Script quirks this code works around:
 *   1. Apps Script Web Apps always return HTTP 200, even for validation errors —
 *      there's no real way to send a 400/500 status. Success/failure is read
 *      from the JSON body's `success` field instead of response.ok.
 *   2. Sending `Content-Type: application/json` triggers a CORS preflight (OPTIONS)
 *      that Apps Script doesn't handle, which fails silently. Sending as
 *      `text/plain` avoids the preflight while still letting Code.gs parse the
 *      body as JSON via e.postData.contents.
 *
 * IMPORTANT: update RSVP_ENDPOINT below to your deployed Apps Script Web App URL
 * (see apps-script/SETUP.md for deployment steps).
 */
(function () {
  'use strict';

  // Replace with your deployed Apps Script Web App URL, e.g.
  // 'https://script.google.com/macros/s/AKfycb.../exec'
  var RSVP_ENDPOINT = 'https://script.google.com/macros/s/AKfycbyHriy3CBk7hzvFl2p-JR-oQCe4eBY-9F9iUBBsuLgecrCJA-Gy2E0yS03KyLjw1CBo4w/exec';

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel));
  }

  function openModal(modal, trigger) {
    var titleEl = qs('[data-rsvp-event-title]', modal);
    var dateEl = qs('[data-rsvp-event-date]', modal);
    var titleField = qs('[data-rsvp-field="event_title"]', modal);

    var eventTitle = trigger.getAttribute('data-event-title') || 'RSVP';
    var eventDate = trigger.getAttribute('data-event-date');

    if (titleEl) titleEl.textContent = eventTitle;
    if (titleField) titleField.value = eventTitle;

    if (dateEl) {
      if (eventDate) {
        var d = new Date(eventDate + 'T00:00:00');
        if (!isNaN(d.getTime())) {
          dateEl.textContent = d.toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          });
        } else {
          dateEl.textContent = '';
        }
      } else {
        dateEl.textContent = '';
      }
    }

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    var firstInput = qs('input[name="first_name"]', modal);
    if (firstInput) {
      setTimeout(function () {
        firstInput.focus();
      }, 50);
    }
  }

  function closeModal(modal) {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    var msg = qs('[data-rsvp-message]', modal);
    if (msg) {
      msg.className = 'hdt-rsvp-message';
      msg.textContent = '';
    }
    var form = qs('[data-rsvp-form]', modal);
    if (form) form.reset();
  }

  function validateForm(form) {
    var requiredFields = qsa('input[required]', form);
    for (var i = 0; i < requiredFields.length; i++) {
      var field = requiredFields[i];
      if (!field.value || !field.value.trim()) {
        field.focus();
        return { valid: false, message: 'Please fill in all required fields.' };
      }
      if (field.type === 'email') {
        var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(field.value)) {
          field.focus();
          return { valid: false, message: 'Please enter a valid email address.' };
        }
      }
    }
    return { valid: true };
  }

  function handleSubmit(e) {
    e.preventDefault();
    var form = e.target;
    var modal = form.closest('.hdt-rsvp-modal');
    var msg = qs('[data-rsvp-message]', modal);
    var submitBtn = qs('[data-rsvp-submit-btn]', form);

    var validation = validateForm(form);
    if (!validation.valid) {
      msg.textContent = validation.message;
      msg.className = 'hdt-rsvp-message is-error';
      return;
    }

    var formData = new FormData(form);
    var payload = {
      event_title: formData.get('event_title'),
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      notification_email: formData.get('notification_email'),
      website: formData.get('website') // honeypot — should always be empty
    };

    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    fetch(RSVP_ENDPOINT, {
      method: 'POST',
      // text/plain avoids a CORS preflight request, which Apps Script Web Apps
      // don't handle. The body is still valid JSON — Code.gs parses it with
      // JSON.parse(e.postData.contents).
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (!data || data.success !== true) {
          throw new Error((data && data.error) || 'Submission failed.');
        }
        msg.textContent = "You're on the list! We'll be in touch with more details.";
        msg.className = 'hdt-rsvp-message is-success';
        form.reset();
        setTimeout(function () {
          closeModal(modal);
        }, 2200);
      })
      .catch(function (err) {
        msg.textContent = (err && err.message) || 'Something went wrong. Please try again or contact us directly.';
        msg.className = 'hdt-rsvp-message is-error';
      })
      .finally(function () {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit RSVP';
      });
  }

  function init() {
    qsa('[data-rsvp-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var section = trigger.closest('[id^="template--"], [id]');
        var sectionId = section ? section.id : null;
        var modal = sectionId ? qs('#hdt-rsvp-modal-' + sectionId) : qs('.hdt-rsvp-modal');
        if (modal) openModal(modal, trigger);
      });
    });

    qsa('.hdt-rsvp-modal').forEach(function (modal) {
      qsa('[data-rsvp-close]', modal).forEach(function (closeBtn) {
        closeBtn.addEventListener('click', function () {
          closeModal(modal);
        });
      });

      var form = qs('[data-rsvp-form]', modal);
      if (form) form.addEventListener('submit', handleSubmit);

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
          closeModal(modal);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
